import { useEffect, useState } from "react";
import { api, formatNOK } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  BarChart3, Users as UsersIcon, XCircle, UserX, CalendarRange, Wallet,
  Sparkles, Download,
} from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { downloadFile } from "@/lib/exportCsv";
import { PANEL_CLS, PAGE_EYEBROW_CLS, PAGE_H1_CLS, PRIMARY_BTN_CLS, SECONDARY_BTN_CLS } from "@/components/premium";

/**
 * Premium-only reports page.
 *
 * Backend endpoint `/api/reports/overview` is the source of truth and is
 * itself plan-gated (returns 402 for non-Premium). When we receive a 402
 * we render an upgrade banner instead of the metrics grid — the page is
 * still routed for everyone so a Pro/Basic Leder who navigates here gets
 * a clear "this is Premium" experience, not a silent 404.
 */
export default function Reports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planBlocked, setPlanBlocked] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/reports/overview");
        if (!alive) return;
        setData(res.data);
      } catch (err) {
        if (!alive) return;
        if (err?.response?.status === 402) {
          setPlanBlocked(true);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) return <ReportsSkeleton />;
  if (planBlocked) return <PremiumGate />;
  if (!data) return <p className="text-muted-foreground">Kunne ikke laste rapporten.</p>;

  return (
    <div className="space-y-6" data-testid="reports-page">
      <SEO title="Rapporter — ClientFlow" path="/rapporter" noindex />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div className="min-w-0">
          <p className={PAGE_EYEBROW_CLS}>Rapporter · Premium</p>
          <h1 className={PAGE_H1_CLS}>Rapporter</h1>
          <p className="mt-2 text-muted-foreground max-w-2xl">
            Faktiske tall fra dine bookinger og kunder — siste 30 og 90 dager.
          </p>
        </div>
        <Button
          variant="outline"
          className={SECONDARY_BTN_CLS}
          onClick={() => downloadFile("/reports/overview.pdf", "clientflow-rapport.pdf", "rapporten")}
          data-testid="reports-pdf-download"
        >
          <Download className="w-4 h-4" strokeWidth={1.75} />
          Last ned PDF
        </Button>
      </div>

      {/* 30-day overview */}
      <Section title="Siste 30 dager">
        <Grid>
          <Stat icon={CalendarRange} label="Bookinger" value={data.bookings_30} testid="r-bookings-30" />
          <Stat icon={UsersIcon} label="Nye kunder" value={data.new_customers_30} testid="r-newcust-30" />
          <Stat icon={XCircle} label="Kansellerte" value={data.cancelled_30} testid="r-cancelled-30" tone="warn" />
          <Stat icon={UserX} label="Ikke møtt" value={data.no_show_30} testid="r-noshow-30" tone="warn" />
          <Stat
            icon={Wallet}
            label="Estimert bookingverdi"
            value={formatNOK(data.estimated_value_30)}
            hint="Sum av priser, ekskl. kansellerte. Ikke bekreftet omsetning."
            testid="r-value-30"
            wide
          />
        </Grid>
      </Section>

      {/* 90-day overview */}
      <Section title="Siste 90 dager">
        <Grid>
          <Stat icon={CalendarRange} label="Bookinger" value={data.bookings_90} testid="r-bookings-90" />
          <Stat icon={UsersIcon} label="Nye kunder" value={data.new_customers_90} testid="r-newcust-90" />
          <Stat icon={XCircle} label="Kansellerte" value={data.cancelled_90} testid="r-cancelled-90" tone="warn" />
          <Stat icon={UserX} label="Ikke møtt" value={data.no_show_90} testid="r-noshow-90" tone="warn" />
          <Stat
            icon={Wallet}
            label="Estimert bookingverdi"
            value={formatNOK(data.estimated_value_90)}
            hint="Sum av priser, ekskl. kansellerte. Ikke bekreftet omsetning."
            testid="r-value-90"
            wide
          />
        </Grid>
      </Section>

      {/* Popular services */}
      <Section title="Mest populære tjenester (siste 90 dager)">
        {data.popular_services?.length ? (
          <Card className={PANEL_CLS}>
            <CardContent className="p-0">
              <ul className="divide-y divide-emerald-500/10" data-testid="r-popular-list">
                {data.popular_services.map((s, idx) => (
                  <li
                    key={`${s.name}-${idx}`}
                    className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 hover:bg-emerald-500/[0.04] transition-colors"
                    data-testid="r-popular-row"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 text-xs font-bold ring-1 ring-emerald-500/30 shrink-0">
                        {idx + 1}
                      </span>
                      <span className="font-medium truncate">{s.name}</span>
                    </div>
                    <span className="text-sm text-muted-foreground shrink-0 tabular-nums">
                      {s.count} bookinger
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ) : (
          <Card className={PANEL_CLS}>
            <CardContent className="p-6 text-center text-sm text-muted-foreground">
              Ingen bookinger registrert i perioden ennå.
            </CardContent>
          </Card>
        )}
      </Section>
    </div>
  );
}

// ---------- Layout helpers ----------
function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <h2 className="font-heading text-lg sm:text-xl font-semibold tracking-tight">{title}</h2>
      {children}
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">{children}</div>;
}

function Stat({ icon: Icon, label, value, hint, testid, tone, wide }) {
  const accentText = tone === "warn" ? "text-amber-500" : "text-emerald-500";
  const accentBg = tone === "warn" ? "bg-amber-500/15 ring-amber-500/30" : "bg-emerald-500/15 ring-emerald-500/30";
  return (
    <Card
      className={`${PANEL_CLS} ${wide ? "col-span-2 lg:col-span-4" : ""} overflow-hidden transition-all hover:ring-emerald-500/20`}
      data-testid={testid}
    >
      <CardContent className="relative p-5">
        <div className={`pointer-events-none absolute -top-12 -right-12 w-32 h-32 rounded-full blur-3xl opacity-90 ${tone === "warn" ? "bg-amber-500/15" : "bg-emerald-500/15"}`} />
        <div className="relative flex items-center justify-between gap-2">
          <span className="text-[11px] uppercase tracking-[0.16em] font-semibold text-muted-foreground">{label}</span>
          <div className={`w-7 h-7 rounded-lg ring-1 flex items-center justify-center ${accentBg} ${accentText}`}>
            <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
          </div>
        </div>
        <div className="relative mt-3 font-heading text-2xl sm:text-3xl font-bold tracking-tight tabular-nums">
          {value ?? "—"}
        </div>
        {hint && <p className="relative text-[11px] text-muted-foreground mt-2 leading-tight">{hint}</p>}
      </CardContent>
    </Card>
  );
}

// ---------- Non-Premium upgrade banner ----------
function PremiumGate() {
  return (
    <div className="space-y-6">
      <SEO title="Rapporter — Premium" path="/rapporter" noindex />
      <Card
        className={`${PANEL_CLS} bg-gradient-to-br from-emerald-500/10 via-card to-card`}
        data-testid="reports-premium-gate"
      >
        <CardContent className="p-6 sm:p-8 flex flex-col sm:flex-row items-start gap-5">
          <div className="w-12 h-12 rounded-2xl bg-emerald-500/15 ring-1 ring-emerald-500/30 flex items-center justify-center text-emerald-500 shrink-0">
            <BarChart3 className="w-6 h-6" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <p className={PAGE_EYEBROW_CLS}>Premium-funksjon</p>
            <h2 className="font-heading text-xl sm:text-2xl font-bold mt-1">Rapporter</h2>
            <p className="text-muted-foreground mt-2 max-w-2xl">
              Premium gir deg detaljerte rapporter på bookinger, nye kunder, kansellerte timer,
              no-shows, mest populære tjenester og estimert bookingverdi — siste 30 og 90 dager.
              Du får også en automatisk månedsrapport på e-post.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                onClick={() => { window.location.href = "/#priser"; }}
                className={PRIMARY_BTN_CLS}
                data-testid="reports-upgrade-cta"
              >
                <Sparkles className="w-4 h-4" strokeWidth={2} /> Se Premium-pakken
              </Button>
              <Link to="/dashboard">
                <Button variant="outline" className="rounded-2xl h-10">Tilbake til dashboard</Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------- Skeleton ----------
function ReportsSkeleton() {
  return (
    <div className="space-y-6" data-testid="reports-loading">
      <div>
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-9 w-48 mt-2" />
      </div>
      {[0, 1].map((s) => (
        <div key={s} className="space-y-3">
          <Skeleton className="h-5 w-40" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-2xl" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
